import { Injectable, computed, inject, signal } from '@angular/core';
import { forkJoin, map, Observable, switchMap, tap } from 'rxjs';
import { ApiService } from '../api.service';
import { JobProfiles, Languages, Norms, Report } from '../models';

@Injectable({ providedIn: 'root' })
export class ReportDomainService {
  private readonly api = inject(ApiService);

  private readonly clientReportsSignal = signal<Report[] | null>(null);
  readonly clientReports = computed(() => this.clientReportsSignal());

  private readonly projectReportsSignal = signal<Report[] | null>(null);
  readonly projectReports = computed(() => this.projectReportsSignal());

  private readonly participantReportsSignal = signal<Report[] | null>(null);
  readonly participantReports = computed(() => this.participantReportsSignal());

  private readonly currentReportSignal = signal<Report | null>(null);
  readonly currentReport = computed(() => this.currentReportSignal());

  private readonly reportLanguagesSignal = signal<Languages | null>(null);
  readonly reportLanguages = computed(() => this.reportLanguagesSignal());

  private readonly reportNormsSignal = signal<Norms | null>(null);
  readonly reportNorms = computed(() => this.reportNormsSignal());

  private readonly reportJobProfilesSignal = signal<JobProfiles | null>(null);
  readonly reportJobProfiles = computed(() => this.reportJobProfilesSignal());

  loadClientReports(): Observable<Report[]> {
    return this.api
      .getClientReports()
      .pipe(tap((reports) => this.clientReportsSignal.set(reports)));
  }

  loadProjectReports(projectId: string): Observable<Report[]> {
    return this.api
      .getProjectReports(projectId)
      .pipe(tap((reports) => this.projectReportsSignal.set(reports)));
  }

  loadParticipantReports(participantId: string): Observable<Report[]> {
    return this.api
      .getParticipantReports(participantId)
      .pipe(tap((reports) => this.participantReportsSignal.set(reports)));
  }

  loadProjectReport(projectId: string, reportId: string): Observable<Report> {
    return this.api
      .getProjectReport(projectId, reportId)
      .pipe(tap((report) => this.currentReportSignal.set(report)));
  }

  loadReportLanguages(reportId: string): Observable<Languages> {
    return this.api
      .getReportLanguages(reportId)
      .pipe(tap((languages) => this.reportLanguagesSignal.set(languages)));
  }

  loadReportNorms(reportId: string): Observable<Norms> {
    return this.api
      .getReportNorms(reportId)
      .pipe(tap((norms) => this.reportNormsSignal.set(norms)));
  }

  loadReportJobProfiles(reportId: string): Observable<JobProfiles> {
    return this.api
      .getReportJobProfiles(reportId)
      .pipe(tap((profiles) => this.reportJobProfilesSignal.set(profiles)));
  }

  addProjectReport(projectId: string, report: Report): Observable<void> {
    return this.api.addProjectReport(projectId, report).pipe(
      tap(() => {
        const current = this.projectReportsSignal();
        if (current) {
          const filtered = current.filter((r) => r.id !== report.id);
          this.projectReportsSignal.set([...filtered, report]);
        } else {
          this.projectReportsSignal.set([report]);
        }
      }),
    );
  }

  deleteProjectReport(projectId: string, reportId: string): Observable<void> {
    return this.api.deleteProjectReport(projectId, reportId).pipe(
      tap(() => {
        const current = this.projectReportsSignal();
        if (current) {
          this.projectReportsSignal.set(current.filter((r) => r.id !== reportId));
        }
      }),
    );
  }

  addParticipantReport(participantId: string, report: Report): Observable<void> {
    return this.api.addParticipantReport(participantId, report).pipe(
      tap(() => {
        const current = this.participantReportsSignal();
        if (current) {
          const filtered = current.filter((r) => r.id !== report.id);
          this.participantReportsSignal.set([...filtered, report]);
        } else {
          this.participantReportsSignal.set([report]);
        }
      }),
    );
  }

  deleteParticipantReport(participantId: string, reportId: string): Observable<void> {
    return this.api.deleteParticipantReport(participantId, reportId).pipe(
      tap(() => {
        const current = this.participantReportsSignal();
        if (current) {
          this.participantReportsSignal.set(current.filter((r) => r.id !== reportId));
        }
      }),
    );
  }

  updateProjectReport(projectId: string, report: Report): Observable<void> {
    return forkJoin([
      this.api.updateProjectReport(projectId, report),
      this.api.updateProjectParticipantReports(projectId, report),
    ]).pipe(
      tap(() => {
        const currentProjectReports = this.projectReportsSignal();
        if (currentProjectReports) {
          this.projectReportsSignal.set(
            currentProjectReports.map((r) => (r.id === report.id ? report : r)),
          );
        }

        const currentParticipantReports = this.participantReportsSignal();
        if (currentParticipantReports) {
          this.participantReportsSignal.set(
            currentParticipantReports.map((r) => (r.id === report.id ? report : r)),
          );
        }
      }),
      map(() => void 0),
    );
  }

  updateParticipantReportAndGenerate(
    clientId: string,
    projectId: string,
    participantId: string,
    report: Report,
  ): Observable<void> {
    return this.api.updateParticipantReport(participantId, report).pipe(
      switchMap(() =>
        this.api.generateParticipantReport(clientId, projectId, participantId, report.id),
      ),
      tap(() => {
        const currentParticipantReports = this.participantReportsSignal();
        if (currentParticipantReports) {
          this.participantReportsSignal.set(
            currentParticipantReports.map((r) => (r.id === report.id ? report : r)),
          );
        }
      }),
      map(() => void 0),
    );
  }
}
